import { getSubscriptionTierLabel, getSubscriptionViewModel } from './subscription-ui.js';

// These immutable policy versions match the current Sogni purchase flow.
export const BILLING_TERMS_VERSION = '2026-08-26';
export const BILLING_REFUND_POLICY_VERSION = '2026-09-09';
export const TRIAL_TERMS_VERSION = 5;
export const BILLING_LINKS = {
  terms: 'https://www.sogni.ai/terms-of-service/versions/2026-08-26',
  privacy: 'https://www.sogni.ai/privacy-policy/versions/2026-08-26',
  refunds: 'https://docs.sogni.ai/pricing/cancellation-and-refund-policy/versions/2026-09-09/'
};

export function formatPlanPrice(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2
  }).format(Number(value));
}

export function planChargeLabel(plan) {
  return `${formatPlanPrice(plan.priceUsd)} per ${plan.term === 'annual' ? 'year' : 'month'}`;
}

export function getPlanPresentation(plan, plans, planContent) {
  const content = planContent?.plans?.[plan.tier || plan.planId];
  const monthly = plans.find((candidate) => candidate.planId === plan.planId && candidate.term === 'monthly');
  const annual = plans.find((candidate) => candidate.planId === plan.planId && candidate.term === 'annual');
  const annualSavings = monthly && annual ? monthly.priceUsd * 12 - annual.priceUsd : 0;
  return {
    ...plan,
    name: getSubscriptionTierLabel(plan.tier || plan.planId),
    content,
    charge: planChargeLabel(plan),
    annualSavings,
    annualSavingsPercent: annualSavings > 0 ? Math.round(annualSavings / (monthly.priceUsd * 12) * 100) : 0,
    monthlyEquivalent: plan.term === 'annual' ? plan.priceUsd / 12 : plan.priceUsd,
    // The comparison describes available benefits, rather than future products.
    detailGroups: (content?.detailGroups || []).filter(({ group }) => group.toLowerCase() !== 'coming soon')
  };
}

export function getPlanAction(subscription, plan, { isUnlimited = false } = {}) {
  const view = getSubscriptionViewModel(subscription, { isUnlimited });
  if (!view.showCurrent) return { kind: 'subscribe', label: 'Review subscription', disabled: false };
  if (view.recovery) {
    return { kind: 'manage', label: view.managementLabel || 'Billing needs attention', disabled: view.management.kind === 'none' };
  }
  if (subscription?.tier === plan.planId && subscription?.term === plan.term) {
    return { kind: 'current', label: 'Current plan', disabled: true };
  }
  if (view.provider === 'apple') return { kind: 'change', label: 'Change in App Store', disabled: false };
  if (view.provider === 'google') return { kind: 'change', label: 'Change in Google Play', disabled: false };
  if (view.provider !== 'stripe') return { kind: 'none', label: 'Contact your billing provider', disabled: true };
  return {
    kind: 'change',
    label: subscription?.tier === 'unlimited' && plan.planId === 'unlimited_pro' ? 'Review upgrade to Pro' : 'Review plan change',
    disabled: false
  };
}

export function buildBillingLegalConsent(now = new Date()) {
  return {
    accepted: true,
    termsVersion: BILLING_TERMS_VERSION,
    refundPolicyVersion: BILLING_REFUND_POLICY_VERSION,
    acceptedAt: now.toISOString()
  };
}

export function buildTrialConsent(plan, decision, now = new Date()) {
  return {
    version: TRIAL_TERMS_VERSION,
    decision,
    acceptedAt: now.toISOString(),
    displayedCharge: planChargeLabel(plan),
    ...(decision === 'accepted_trial' ? {
      holdAccepted: true,
      holdAmountMinor: Math.round(plan.priceUsd * 100),
      holdCurrency: 'usd',
      displayedHold: formatPlanPrice(plan.priceUsd),
      holdStatementDescriptor: 'SOGNI* NO CHARGE'
    } : {})
  };
}

export function getTrialTerms(plan, planContent) {
  const content = planContent?.plans?.[plan.tier || plan.planId];
  const trialLimits = (content?.detailGroups || [])
    .filter(({ group }) => /trial/i.test(group))
    .flatMap(({ items }) => items)
    .join(' ');
  return [
    { title: 'The trial is capped on purpose', body: trialLimits },
    {
      title: 'Free trials require an active credit or debit card',
      body: 'Please use a regular credit or debit card. Prepaid, virtual and other specialty cards are commonly declined because the trial verification must confirm the card can cover a subscription payment. Those cards can still be used for a regular paid subscription.'
    },
    {
      title: 'A SOGNI* NO CHARGE alert is a temporary hold, not a charge',
      body: 'By starting the trial, you authorize Sogni to run a quick card check. Your bank may send an alert from “SOGNI* NO CHARGE” showing the selected plan amount and describe it as a charge, but no payment is collected.'
    },
    {
      title: 'A declined card simply cancels the trial',
      body: 'If your card provider declines the hold, we cancel the trial and nothing is charged. You are welcome to start a new trial with a different card — a trial that never started does not count as one you have used. If verification needs another step or cannot be completed, trial access stays unavailable; reply to our verification email for help.'
    },
    {
      title: 'Cancelling stops renewal; your trial continues',
      body: 'Cancel before the trial ends to avoid the first charge. You keep trial access, with the same daily limits, until the original trial end date — even if you cancel right away. Cancellation does not extend the trial or make you eligible for another one.'
    },
    {
      title: 'One trial per person and per device',
      body: 'Once you have used a trial, starting further trials — on a new account, a new card or the same device — cancels every trial involved and may suspend the linked accounts.'
    },
    {
      title: 'It becomes a paid subscription unless you cancel',
      body: `When the ${content?.trialDays} days are up, billing starts automatically at ${planChargeLabel(plan)}. Cancel any time before then from your subscription controls and you will not be charged. Once billing has started, refunds are not guaranteed, particularly after substantial use. Where permitted by applicable law and payment-provider rules, a discretionary refund may reflect non-recoverable transaction costs; we will disclose the amount before processing.`
    }
  ];
}
